import { Helmet } from "react-helmet-async";

import { helmetData } from "@/client/constants/helmet";

export const CookiesPage = () => (
  <>
    <Helmet prioritizeSeoTags helmetData={helmetData}>
      <title>Cookie Policy - CareerBldr</title>
      <meta
        name="description"
        content="Cookie Policy for CareerBldr. Learn how we use cookies and similar technologies on our platform."
      />
    </Helmet>

    <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
      <header className="mb-10">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Cookie Policy</h1>
        <p className="mt-2 text-xs text-foreground/50">Effective Date: September 3, 2026</p>
      </header>

      <div className="space-y-8">
        <section>
          <p className="text-sm leading-relaxed text-foreground/70">
            CareerBldr (&quot;the Service&quot;), operated by Studio Algorithm (&quot;we,&quot;
            &quot;us,&quot; or &quot;our&quot;), uses cookies and similar technologies on our
            website and platform. This Cookie Policy explains what these technologies are, why we
            use them, and your choices regarding their use.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">1. What Are Cookies?</h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            Cookies are small text files placed on your device when you visit a website. They are
            widely used to make websites work more efficiently and to provide reporting information.
            Cookies set by the website owner are called &quot;first-party cookies.&quot; Cookies set
            by parties other than the website owner are called &quot;third-party cookies&quot; and
            enable third-party features or functionality.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">2. Cookies We Use</h2>
          <p className="mb-4 text-sm leading-relaxed text-foreground/70">
            We use the following categories of cookies:
          </p>

          <div className="space-y-4">
            <div className="rounded-lg border border-border/50 p-4">
              <h3 className="text-sm font-semibold text-foreground">Strictly Necessary Cookies</h3>
              <p className="mt-1 text-xs leading-relaxed text-foreground/60">
                These cookies are essential for the website to function. They include authentication
                cookies (to keep you signed in), session cookies, and CSRF protection tokens.
                Without these cookies, the Service cannot operate properly. These cookies do not
                collect personal information for marketing purposes.
              </p>
            </div>

            <div className="rounded-lg border border-border/50 p-4">
              <h3 className="text-sm font-semibold text-foreground">Functional Cookies</h3>
              <p className="mt-1 text-xs leading-relaxed text-foreground/60">
                These cookies remember your preferences and settings, such as your language
                preference, theme (light/dark mode), and locale settings. They enhance your
                experience by personalizing content and are stored locally using browser storage
                APIs.
              </p>
            </div>

            <div className="rounded-lg border border-border/50 p-4">
              <h3 className="text-sm font-semibold text-foreground">Analytics Cookies</h3>
              <p className="mt-1 text-xs leading-relaxed text-foreground/60">
                These cookies help us understand how visitors interact with our website by
                collecting and reporting information. We use this data to improve the Service,
                understand popular features, measure advertising performance, and identify areas for
                improvement. When you create an account, begin checkout or a trial, or subscribe
                after clicking an ad, we may use Google, OpenAI Ads, and TikTok Ads to measure that
                conversion.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">
            3. Third-Party Cookies
          </h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            We may use third-party services that set their own cookies, including Stripe (for
            payment processing), Sentry (for error monitoring), Google Analytics, Google Ads, OpenAI
            Ads, and TikTok Ads. TikTok may set the <code>_ttp</code> cookie, and we retain a TikTok
            click identifier when present for conversion attribution. These third parties have their
            own privacy policies governing how they use cookies. We do not control these third-party
            cookies and recommend reviewing their respective privacy policies.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">
            4. Your Cookie Choices
          </h2>
          <p className="mb-3 text-sm leading-relaxed text-foreground/70">
            You have several options for managing cookies:
          </p>
          <ul className="space-y-2 text-sm leading-relaxed text-foreground/70">
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>
                <strong className="font-medium text-foreground">Browser Settings</strong>: Most
                browsers allow you to refuse or accept cookies and to delete existing cookies. Check
                your browser&apos;s help section for instructions.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>
                <strong className="font-medium text-foreground">Opt-Out Links</strong>: Some
                analytics providers offer opt-out mechanisms. For example, you can opt out of Google
                Analytics by installing the browser add-on.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>
                <strong className="font-medium text-foreground">Do Not Track</strong>: We honor Do
                Not Track browser signals where technically feasible.
              </span>
            </li>
          </ul>
          <p className="mt-3 text-sm leading-relaxed text-foreground/70">
            Please note that blocking or deleting strictly necessary cookies may affect the
            functionality of the Service, including your ability to sign in or use certain features.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">
            5. Data Retention for Cookies
          </h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            Session cookies are deleted when you close your browser. Persistent cookies remain on
            your device for a set period or until you delete them. Authentication cookies typically
            expire after 7 days of inactivity. Functional preference cookies may persist for up to 1
            year.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">
            6. Updates to This Policy
          </h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            We may update this Cookie Policy from time to time to reflect changes in our practices
            or applicable regulations. Any changes will be posted on this page with an updated
            effective date. We encourage you to review this policy periodically.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">7. Contact</h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            If you have questions about our use of cookies, please contact us at{" "}
            <a
              href="mailto:privacy@careerbldr.com"
              className="font-medium text-primary underline decoration-primary/30 underline-offset-2 transition-colors hover:text-primary/80"
            >
              privacy@careerbldr.com
            </a>
            .
          </p>
          <p className="mt-4 text-sm leading-relaxed text-foreground/70">
            See also our{" "}
            <a
              href="/privacy"
              className="font-medium text-primary underline decoration-primary/30 underline-offset-2 transition-colors hover:text-primary/80"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href="/terms"
              className="font-medium text-primary underline decoration-primary/30 underline-offset-2 transition-colors hover:text-primary/80"
            >
              Terms of Service
            </a>
            .
          </p>
          <p className="mt-4 text-sm leading-relaxed text-foreground/70">
            CareerBldr is operated by Studio Algorithm.
          </p>
        </section>
      </div>
    </div>
  </>
);
